import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sparkles, GripVertical, ChevronDown, ChevronRight, ExternalLink, Pencil, Trash2, Plus, StickyNote } from "lucide-react";
import { Link } from "wouter";
import {
  ENHANCEMENT_ICON_KEYS, getEnhancementIcon, DEFAULT_ENHANCEMENT_ICON_KEY,
  type PlatformEnhancement,
} from "@/lib/enhancements-data";
import { useToast } from "@/hooks/use-toast";

type EnhancementForm = {
  name: string;
  description: string;
  reenable: string;
  iconKey: string;
  status: "hidden" | "disabled";
  previewHref: string;
  note: string;
};

const emptyForm: EnhancementForm = {
  name: "",
  description: "",
  reenable: "",
  iconKey: DEFAULT_ENHANCEMENT_ICON_KEY,
  status: "hidden",
  previewHref: "",
  note: "",
};

export function EnhancementsContent() {
  const { toast } = useToast();
  const [items, setItems] = useState<PlatformEnhancement[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Edit / Add dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EnhancementForm>(emptyForm);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<PlatformEnhancement | null>(null);

  const { data, isLoading } = useQuery<{ enhancements: PlatformEnhancement[] }>({
    queryKey: ["/api/platform/enhancements"],
  });

  useEffect(() => {
    if (data?.enhancements) {
      setItems([...data.enhancements].sort((a, b) => a.sortOrder - b.sortOrder));
    }
  }, [data]);

  const orderMutation = useMutation({
    mutationFn: (updates: { id: string; sortOrder: number; showInWorkspaces?: boolean }[]) =>
      apiRequest("PUT", "/api/platform/enhancements/order", { updates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/enhancements"] });
    },
    onError: () => {
      toast({ title: "Save failed", description: "Could not save changes.", variant: "destructive" });
    },
  });

  const saveOrder = useCallback((updates: { id: string; sortOrder: number; showInWorkspaces?: boolean }[]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => orderMutation.mutate(updates), 400);
  }, [orderMutation]);

  const createMutation = useMutation({
    mutationFn: (payload: Partial<EnhancementForm>) =>
      apiRequest("POST", "/api/platform/enhancements", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/enhancements"] });
      toast({ title: "Enhancement added" });
      setEditOpen(false);
    },
    onError: () => {
      toast({ title: "Add failed", description: "Could not create enhancement.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/platform/enhancements/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/enhancements"] });
    },
    onError: () => {
      toast({ title: "Save failed", description: "Could not update enhancement.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/enhancements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/enhancements"] });
      toast({ title: "Enhancement deleted" });
      setDeleteTarget(null);
    },
    onError: () => {
      toast({ title: "Delete failed", description: "Could not delete enhancement.", variant: "destructive" });
    },
  });

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleShow = (id: string, checked: boolean) => {
    const next = items.map((it) => (it.id === id ? { ...it, showInWorkspaces: checked } : it));
    setItems(next);
    saveOrder(next.map((it, i) => ({ id: it.id, sortOrder: i, showInWorkspaces: it.showInWorkspaces })));
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragOverId) setDragOverId(id);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const ids = items.map((f) => f.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    const reordered = [...items];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const next = reordered.map((it, i) => ({ ...it, sortOrder: i }));
    setItems(next);
    saveOrder(next.map((it) => ({ id: it.id, sortOrder: it.sortOrder, showInWorkspaces: it.showInWorkspaces })));
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setEditOpen(true);
  };

  const openEdit = (f: PlatformEnhancement) => {
    setEditingId(f.id);
    setForm({
      name: f.name,
      description: f.description ?? "",
      reenable: f.reenable ?? "",
      iconKey: f.iconKey || DEFAULT_ENHANCEMENT_ICON_KEY,
      status: f.status,
      previewHref: f.previewHref ?? "",
      note: f.note ?? "",
    });
    setEditOpen(true);
  };

  const submitForm = () => {
    if (!form.name.trim()) {
      toast({ title: "Name required", description: "Please enter a title.", variant: "destructive" });
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description,
      reenable: form.reenable,
      iconKey: form.iconKey,
      status: form.status,
      previewHref: form.previewHref.trim() ? form.previewHref.trim() : null,
      note: form.note.trim() ? form.note : null,
    };
    if (editingId) {
      updateMutation.mutate(
        { id: editingId, payload },
        { onSuccess: () => { setEditOpen(false); toast({ title: "Enhancement updated" }); } },
      );
    } else {
      createMutation.mutate(payload);
    }
  };

  const visibleCount = items.filter((s) => s.showInWorkspaces).length;
  const formIcon = getEnhancementIcon(form.iconKey);
  const FormIcon = formIcon;

  return (
    <div className="p-6 space-y-5" data-testid="page-platform-enhancements">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Enhancements</h1>
            <p className="text-xs text-muted-foreground">
              Parked features — drag to reorder, toggle to show in tenant workspaces. Double-click to read details.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          {orderMutation.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
          <Badge variant="secondary" className="text-xs">
            {visibleCount} shown in workspaces
          </Badge>
          <Button size="sm" onClick={openAdd} data-testid="button-add-enhancement">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Enhancement
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        {items.map((f) => {
          const Icon = getEnhancementIcon(f.iconKey);
          const isExpanded = expandedIds.has(f.id);
          const isDragging = draggedId === f.id;
          const isDragOver = dragOverId === f.id && draggedId !== f.id;
          const isVisible = f.showInWorkspaces;

          return (
            <div
              key={f.id}
              draggable
              onDragStart={(e) => handleDragStart(e, f.id)}
              onDragOver={(e) => handleDragOver(e, f.id)}
              onDrop={(e) => handleDrop(e, f.id)}
              onDragEnd={handleDragEnd}
              data-testid={`enhancement-${f.id}`}
              className={[
                "transition-all duration-150",
                isDragging ? "opacity-40" : "",
                isDragOver ? "ring-2 ring-primary ring-offset-1 rounded-lg" : "",
              ].join(" ")}
            >
              <Card className={["select-none", isVisible ? "border-primary/30 bg-primary/[0.02]" : ""].join(" ")}>
                {/* Compact header row — always visible */}
                <div
                  className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                  onDoubleClick={() => toggleExpanded(f.id)}
                  title="Double-click to expand details"
                  data-testid={`enhancement-header-${f.id}`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground/50 shrink-0 cursor-grab active:cursor-grabbing" />

                  <button
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(f.id); }}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    data-testid={`button-expand-${f.id}`}
                  >
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5" />
                      : <ChevronRight className="w-3.5 h-3.5" />
                    }
                  </button>

                  <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium flex-1 truncate">{f.name}</span>

                  {f.note && (
                    <StickyNote
                      className="w-3.5 h-3.5 text-amber-500 shrink-0"
                      aria-label="Has a note"
                      data-testid={`note-indicator-${f.id}`}
                    />
                  )}

                  <Badge
                    variant={f.status === "hidden" ? "secondary" : "outline"}
                    className="capitalize text-xs px-1.5 py-0 shrink-0"
                  >
                    {f.status}
                  </Badge>

                  {/* Edit / Delete actions */}
                  <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => openEdit(f)}
                      title="Edit"
                      data-testid={`button-edit-${f.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(f)}
                      title="Delete"
                      data-testid={`button-delete-${f.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  {/* Show in workspaces toggle */}
                  <div
                    className="flex items-center gap-1.5 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    title={isVisible ? "Shown in workspaces — click to hide" : "Hidden from workspaces — click to show"}
                  >
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {isVisible ? "Visible" : "Hidden"}
                    </span>
                    <Switch
                      checked={isVisible}
                      onCheckedChange={(checked) => toggleShow(f.id, checked)}
                      data-testid={`toggle-show-${f.id}`}
                    />
                  </div>
                </div>

                {/* Expanded details — shown on double-click */}
                {isExpanded && (
                  <CardContent className="pt-0 pb-3 px-3 space-y-2 border-t border-border/50 text-sm">
                    <p className="text-muted-foreground text-xs leading-relaxed pt-2">{f.description}</p>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                        How to re-enable
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">{f.reenable}</p>
                    </div>
                    {f.note && (
                      <div className="rounded-md bg-amber-500/10 border border-amber-500/20 px-2.5 py-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-1">
                          <StickyNote className="w-3 h-3" />
                          Note
                        </div>
                        <p className="text-xs leading-relaxed whitespace-pre-wrap" data-testid={`note-text-${f.id}`}>{f.note}</p>
                      </div>
                    )}
                    {f.previewHref && (
                      <Link
                        href={f.previewHref}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`preview-${f.id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Preview the hidden feature
                      </Link>
                    )}
                  </CardContent>
                )}
              </Card>
            </div>
          );
        })}
        {!isLoading && items.length === 0 && (
          <p className="text-xs text-muted-foreground py-6 text-center">
            No enhancements yet. Use “Add Enhancement” to create the first card.
          </p>
        )}
      </div>

      {/* Edit / Add dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Enhancement" : "Add Enhancement"}</DialogTitle>
            <DialogDescription>
              Manage the parked-feature card. Changes are saved to the platform store.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="enh-name">Title</Label>
              <Input
                id="enh-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Feature name"
                data-testid="input-enhancement-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="enh-icon">Icon</Label>
                <Select value={form.iconKey} onValueChange={(v) => setForm({ ...form, iconKey: v })}>
                  <SelectTrigger id="enh-icon" data-testid="select-enhancement-icon">
                    <SelectValue>
                      <span className="flex items-center gap-2">
                        <FormIcon className="w-4 h-4" />
                        {form.iconKey}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {ENHANCEMENT_ICON_KEYS.map((key) => {
                      const KeyIcon = getEnhancementIcon(key);
                      return (
                        <SelectItem key={key} value={key} data-testid={`icon-option-${key}`}>
                          <span className="flex items-center gap-2">
                            <KeyIcon className="w-4 h-4" />
                            {key}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enh-status">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as "hidden" | "disabled" })}>
                  <SelectTrigger id="enh-status" data-testid="select-enhancement-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hidden">Hidden</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enh-desc">Description</Label>
              <VoiceToTextTextarea
                id="enh-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="What this parked feature does…"
                data-testid="input-enhancement-description"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enh-reenable">How to re-enable</Label>
              <VoiceToTextTextarea
                id="enh-reenable"
                value={form.reenable}
                onChange={(e) => setForm({ ...form, reenable: e.target.value })}
                rows={4}
                placeholder="Actionable steps to re-enable this feature…"
                data-testid="input-enhancement-reenable"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enh-preview">Preview link (optional)</Label>
              <Input
                id="enh-preview"
                value={form.previewHref}
                onChange={(e) => setForm({ ...form, previewHref: e.target.value })}
                placeholder="/admin/example"
                data-testid="input-enhancement-preview"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enh-note">Note (optional)</Label>
              <VoiceToTextTextarea
                id="enh-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={3}
                placeholder="Freeform note — context, reminders, decisions…"
                data-testid="input-enhancement-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="button-cancel-enhancement">
              Cancel
            </Button>
            <Button
              onClick={submitForm}
              disabled={createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-enhancement"
            >
              {editingId ? "Save changes" : "Add enhancement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enhancement?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be permanently removed from the enhancements list
              {deleteTarget?.showInWorkspaces ? " and will no longer appear in tenant workspaces" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardContent className="pt-4 pb-3 text-xs text-muted-foreground">
          <strong>Managing cards.</strong> Edit, add, delete, reorder, and note your parked features right here —
          no code changes needed. Toggle <strong>Show in workspaces</strong> to surface upcoming features to
          tenants in their BetaTest Feedback → Upcoming panel.
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlatformEnhancementsPage() {
  return <PlatformLayout><EnhancementsContent /></PlatformLayout>;
}
